'use client';

import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import { ReactNode, useState } from 'react';
import ModalForm from '../Modal/Modal';
import React from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

export interface TeacherBlockBlockProps {
  title: ReactNode | SerializedEditorState;
  infoArr?: Array<{
    title: ReactNode | SerializedEditorState;
    subtitle: ReactNode | SerializedEditorState;
    srcImage: string | { url: string; alt: string };
  }>;
  btnTag?: string;
  buttonText?: string;
  color?: 'violet' | 'green' | 'blue';
}

export default function TeacherBlock({
  title,
  infoArr = [],
  btnTag,
  buttonText,
  color = 'green',
}: TeacherBlockBlockProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const handlePrevClick = () => {
    setCurrentIndex(prevIndex => (prevIndex === 0 ? infoArr.length - 1 : prevIndex - 1));
  };

  const handleNextClick = () => {
    setCurrentIndex(prevIndex => (prevIndex === infoArr.length - 1 ? 0 : prevIndex + 1));
  };

  const currentInfo = infoArr[currentIndex];

  const backgroundHoverColorClass =
    {
      green: 'custom-gradient-green-btn-h',
      blue: 'custom-gradient-blue-btn-h',
      violet: 'custom-gradient-violet-btn-h',
    }[color] || 'custom-gradient-green-btn-h';

  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn',
      blue: 'custom-gradient-blue-btn',
      violet: 'custom-gradient-violet-btn',
    }[color] || 'custom-gradient-green-btn';

  return (
    <>
      <div data-meta="TeacherBlock" className="max-w-7xl my-14 mx-auto">
        <div className="px-4 py-4 lg:py-5 sm:mx-6 lg:mx-8 rounded-3xl bg-white shadow">
          <div className="py-2 px-4 sm:px-6 flex justify-between">
            <div className="text-4xl font-bold">
              <CustomRichText data={title} tag="div" />
            </div>
            <div className="flex gap-x-2">
              <button
                onClick={handlePrevClick}
                className={`p-2 border border-gray-400 text-gray-400 rounded-full hover:border-white hover:text-white ${backgroundHoverColorClass}`}
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <button
                onClick={handleNextClick}
                className={`p-2 border border-gray-400 text-gray-400 rounded-full hover:border-white hover:text-white ${backgroundHoverColorClass}`}
              >
                <ArrowRightIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="bg-[#F5F5F7] rounded-3xl flex flex-col justify-between md:flex-row">
            <div className="max-w-4xl px-4 py-5 sm:p-6 flex flex-col justify-around">
              <div>
                <div className="text-xl font-bold mb-6">
                  <CustomRichText data={currentInfo.title} tag="div" />
                </div>
                <div>
                  <CustomRichText data={currentInfo.subtitle} tag="div" />
                </div>
              </div>
              <button
                onClick={openForm}
                className={`${backgroundColorClass} hidden md:block uppercase mt-4 max-w-min text-white rounded-full px-8 py-2.5 text-center text-sm font-fixel font-bold shadow-sm focus-visible:outline`}
              >
                {buttonText}
              </button>
            </div>
            <div>
              <div className="">
                <div className="xl:w-[750px] xl:h-[500px] w-full h-auto">
                  <Image
                    alt={''}
                    src={
                      typeof currentInfo?.srcImage === 'string'
                        ? currentInfo?.srcImage
                        : currentInfo.srcImage.url || '/placeholder.jpg'
                    }
                    sizes="100vw"
                    width={750}
                    height={500}
                    style={{
                      width: '100%',
                      height: 'auto',
                    }}
                    className="aspect-[3/2] w-full rounded-2xl object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="my-4 md:hidden">
            <button
              onClick={openForm}
              className={`${backgroundColorClass} uppercase mt-4 w-full text-white rounded-full px-8 py-2.5 text-center text-sm font-fixel font-bold shadow-sm focus-visible:outline`}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
      {isFormOpen && (
        <ModalForm closeForm={closeForm} btnMeta={{ tag: btnTag ?? '' }} color={color} />
      )}
    </>
  );
}
